import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useWebSocket } from './WebSocketContext';
import { useTranslation } from "./TranslationContext";
import { useNotification } from './NotificationContext';
import { ConnectionEventEmitter } from '../utils/ConnectionEventEmitter';
import Cookies from 'js-cookie';
import log from "loglevel";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { isConnected, sendMessage, setShowConnecting } = useWebSocket();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [group, setGroup] = useState('default');
  const [user, setUser] = useState({
    name: '',
    email: '',
    age: null,
  });

  const login = (email,phone,password,rememberMe) => {
    sendMessage({
      area: 'user',
      command: 'login',
      ...(email ? { email } : { phone }),
      password: password,
      rememberMe: rememberMe
    });
  };

  const waitingList = (email,phone,name,surname) => {
    const payload = {
      area: 'user',
      command: 'waitingList',
      name: name,
      surname: surname,
      email: email,
      phone: phone,
      browser: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      userAgent: navigator.userAgent,
      preferredLanguage: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    sendMessage(payload);
  };

  const create = (email,phone,name,surname,password,verificationCode) => {
    const payload = {
      area: 'user',
      command: 'create',
      code: verificationCode,
      name: name,
      surname: surname,
      password: password,
      ...(email ? { email } : { phone }),
      browser: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      userAgent: navigator.userAgent,
      preferredLanguage: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    sendMessage(payload);
  };

  const logout = () => {
    sendMessage({
      area: 'user',
      command: 'logout',
      ...(user.email && { email: user.email }),
      ...(user.phone && { phone: user.phone }),
      ...(getSessionId() && { sessionId: getSessionId() })
    });  
  };

  const close = () => {
    sendMessage({
      area: 'user',
      command: 'close',
      ...(user.email && { email: user.email }),
      ...(user.phone && { phone: user.phone }),
      ...(getSessionId() && { sessionId: getSessionId() })
    }); 
  };

  const getUserData = () => {
    return user;
  };

  const getGroup = () => {
    return group;
  };

  const getSessionId = () => {
    let userSessionId = Cookies.get('userSessionId');
    return userSessionId;
  };

  const connectionOpened = useCallback(() => {
    log.debug('AuthContext websocketOpened');
    
    let userSessionId = getSessionId();
    if(userSessionId) {
      log.debug("Found session, trying to login: "+userSessionId);
      setShowConnecting(true);
      sendMessage({
        area: 'user',
        command: 'login',
        sessionId: userSessionId
      });
    } else {
      setShowConnecting(false);
    }
  }, []);

  const connectionClosed = useCallback(() => {
    log.debug('AuthContext websocketClosed');
  }, []);

  const connectionReceived = useCallback((receivedMessage) => {
    if(receivedMessage) {
      log.debug('AuthContext received message:', receivedMessage);

      if(receivedMessage.area==='user') {
        switch (receivedMessage.command) {
          case 'create':
            setShowConnecting(false);
            if(receivedMessage.status==='ok') {
              Cookies.set('userSessionId', receivedMessage.sessionId);
              setGroup(receivedMessage.group);
              setUser(receivedMessage.user);
              setIsAuthenticated(true);

              const lastVisitedPath = localStorage.getItem('lastVisitedPath') || '/profile';
              log.debug("lastVisitedPath: "+lastVisitedPath);
              navigate(lastVisitedPath);           
            } else {
              showNotification(t.login.CantCreateAccount);
            }
            break;
          case 'login':
            setShowConnecting(false);
            if(receivedMessage.status==='ok') {
              Cookies.set('userSessionId', receivedMessage.sessionId);
              setGroup(receivedMessage.group);
              setUser(receivedMessage.user);
              setIsAuthenticated(true);

              const lastVisitedPath = localStorage.getItem('lastVisitedPath') || '/profile';
              log.debug("lastVisitedPath: "+lastVisitedPath);
              navigate(lastVisitedPath);           
            } else {
              if(receivedMessage.status==='deactivated') {
                showNotification(t.login.LoginDeactivated);
              } else {
                showNotification(t.login.CantLogin);
              }
            }
            break;          
          case 'logout':
            if(receivedMessage.status==='ok') {
              Cookies.remove('userSessionId');
              setIsAuthenticated(false);
              navigate('/login');
            } 
            break;
          case 'close':
            if(receivedMessage.status==='ok') {
              Cookies.remove('userSessionId');
              setIsAuthenticated(false);
              navigate('/login');
            } 
            break;
        }
      }
    }
  }, []);

  useEffect(() => {    
    ConnectionEventEmitter.on('opened', connectionOpened);
    ConnectionEventEmitter.on('closed', connectionClosed);
    ConnectionEventEmitter.on('received', connectionReceived);

    if(isConnected) {
      let userSessionId = getSessionId();
      log.debug("Found session, trying to login: "+userSessionId);
      if(userSessionId) {
        sendMessage({
          area: 'user',
          command: 'login',
          sessionId: userSessionId
        });
      }
    }
    return () => { 
      ConnectionEventEmitter.off('opened', connectionOpened);
      ConnectionEventEmitter.off('closed', connectionClosed);
      ConnectionEventEmitter.off('received', connectionReceived);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, create, logout, close, getUserData, getGroup, getSessionId, waitingList }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);